import { useState, useEffect, useMemo, useRef } from "react"
import SelectField from '../common/SelectField.js';
import { useParams, useNavigate, useSearchParams } from "react-router-dom"
import { mlSheetService } from "../../services/mlSheetService"
import { shipmentService } from "../../services/shipmentService"
import companySettingsService from "../../services/companySettingsService.js"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader"
import Alert from "../common/Alert"
import { ArrowLeft, Package, RefreshCw, Undo2, Ban } from "lucide-react";
import Button from '../common/Button.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import DeliveryOtpModal from '../common/DeliveryOtpModal.js';

import { shipmentMatchesSearch } from "../../utils/mlSheetShipmentSearch.js"
import { printShipmentReceipts } from "../../utils/printShipmentReceipt.js"
import { getBulkStatusOptions, requiresDeliveryCollection } from "../../utils/shipmentPayment.js"
import { canRevertDelivery, revertDeliveryDisabledReason } from "../../utils/deliveryRevert.js"
import { isOtpRequiredResponse, normalizeOtpPending } from "../../utils/deliveryOtp.js"
import { canUpdateMlSheetStatus, isContractPartyAdminRole } from "../../utils/mlSheetAgentAccess.js"
import { useTranslation } from 'react-i18next';
import { DEFAULT_LABOUR_CHARGE_POLICY, normalizeLabourChargePolicy, showLabourOnStatus } from '../../utils/labourChargePolicy.js';
import CapturePhotoField from "../common/CapturePhotoField.js"


const isStatusUpdateLocked = (status) =>
  String(status || "").toLowerCase() === "delivered"

const isAgentOwnedHub = (shipment) =>
  String(shipment?.receiver_location_hub?.ownership_type || "").toUpperCase() === "AGENT"

const STATUS_SUBMIT_SELECTOR = '[data-ml-status-submit="true"]'

const focusVisibleSubmit = () => {
  const buttons = document.querySelectorAll(STATUS_SUBMIT_SELECTOR)
  const visible = Array.from(buttons).find((el) => el.offsetParent !== null)
  const target = visible || buttons[0]
  if (target && !target.disabled) target.focus()
}

const onEnter = (next) => (event) => {
  if (event.key !== "Enter") return
  event.preventDefault()
  if (typeof next === "function") next()
  else next?.current?.focus()
}

const StatusUpdate = () => {
  const { t } = useTranslation();
  const { mlSheetId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()

  const [mlSheet, setMLSheet] = useState(null)
  const [shipments, setShipments] = useState([])
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)

  // Selection state
  const [selectedShipments, setSelectedShipments] = useState(new Set())
  const [searchQuery, setSearchQuery] = useState("")

  // Status update form
  const [selectedStatus, setSelectedStatus] = useState("Received")
  const [remarks, setRemarks] = useState("Status updated via bulk update")

  // ADD THESE NEW LINES RIGHT AFTER:
  const [deliveryReceiver, setDeliveryReceiver] = useState("")
  const [deliveryReceiverContact, setDeliveryReceiverContact] = useState("")
  const [deliveryReceiverImage, setDeliveryReceiverImage] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [deliveryGovIdImage, setDeliveryGovIdImage] = useState(null)
  const [govIdPreview, setGovIdPreview] = useState(null)
  const [unloadingCharges, setUnloadingCharges] = useState("")
  const [loadingCharges, setLoadingCharges] = useState("")
  const [deliveryVehicleNo, setDeliveryVehicleNo] = useState("")
  const [lastMileHours, setLastMileHours] = useState("2")
  const [labourPolicy, setLabourPolicy] = useState(DEFAULT_LABOUR_CHARGE_POLICY)
  const [chargeDefaults, setChargeDefaults] = useState({})
  const [otpPending, setOtpPending] = useState([])
  const [revertingId, setRevertingId] = useState("")
  const deliveryNameRef = useRef(null)
  const deliveryContactRef = useRef(null)
  const deliveryFocusKeyRef = useRef("")

  const [isMobileLayout, setIsMobileLayout] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  )

  const bulkStatusConfig = useMemo(
    () => getBulkStatusOptions(selectedShipments, shipments),
    [selectedShipments, shipments],
  )

  const statusOptions = useMemo(
    () => bulkStatusConfig.options.map((status) => ({ value: status, label: status })),
    [bulkStatusConfig.options],
  )

  const selectedList = useMemo(
    () =>
      Array.from(selectedShipments)
        .map((id) => shipments.find((s) => s.shipment_id === id))
        .filter(Boolean),
    [selectedShipments, shipments],
  )
  const agentHubSelected = useMemo(
    () => selectedList.filter((s) => isAgentOwnedHub(s)),
    [selectedList],
  )
  const showUnloadingInput = showLabourOnStatus(
    labourPolicy,
    'unloading',
    selectedStatus,
    {
      agentHub: agentHubSelected.length > 0,
      chargeEnabled: !!chargeDefaults.unloading?.enabled,
    },
  )
  const showLoadingInput = showLabourOnStatus(
    labourPolicy,
    'loading',
    selectedStatus,
    {
      agentHub: agentHubSelected.length > 0,
      chargeEnabled: !!chargeDefaults.loading?.enabled,
    },
  )

  useEffect(() => {
    companySettingsService.getSettings()
      .then((res) => {
        setLabourPolicy(normalizeLabourChargePolicy(res?.data?.labour_charge_policy))
        setChargeDefaults(res?.data?.shipment_charge_defaults || {})
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!showUnloadingInput) {
      setUnloadingCharges("")
      return
    }
    const existing = Number(agentHubSelected[0]?.unloading_charges || selectedList[0]?.unloading_charges || 0)
    setUnloadingCharges(existing > 0 ? String(existing) : "")
  }, [showUnloadingInput, agentHubSelected, selectedList])

  useEffect(() => {
    if (!showLoadingInput) {
      setLoadingCharges("")
      return
    }
    const existing = Number(selectedList[0]?.loading_charges || 0)
    setLoadingCharges(existing > 0 ? String(existing) : "")
  }, [showLoadingInput, selectedList])

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)")
    const handler = (e) => setIsMobileLayout(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  useEffect(() => {
    const urlStatus = String(searchParams.get("status") || "").toLowerCase()
    if (urlStatus === "delivered") return
    if (bulkStatusConfig.autoSelect) {
      setSelectedStatus(bulkStatusConfig.autoSelect)
      return
    }
    setSelectedStatus((prev) => {
      if (bulkStatusConfig.options.includes(prev)) return prev
      return bulkStatusConfig.options[0] || "Received"
    })
  }, [bulkStatusConfig, searchParams])

  useEffect(() => {
    const selectId = searchParams.get("select")
    if (!selectId || !shipments.length) return
    const match = shipments.find((s) => String(s.shipment_id) === String(selectId))
    if (match && String(match.status || "").toLowerCase() !== "delivered") {
      setSelectedShipments(new Set([match.shipment_id]))
    }
    if (String(searchParams.get("status") || "").toLowerCase() === "delivered") {
      setSelectedStatus("Delivered")
    }
  }, [searchParams, shipments])

  useEffect(() => {
    window.__jabbarStatusSelectedCount = selectedShipments.size
    const onSet = () => {
      if (selectedShipments.size > 0) setSelectedStatus("Delivered")
    }
    window.addEventListener("jabbar-set-delivered", onSet)
    return () => {
      window.removeEventListener("jabbar-set-delivered", onSet)
      window.__jabbarStatusSelectedCount = 0
    }
  }, [selectedShipments])

  useEffect(() => {
    if (loading || selectedStatus !== "Delivered" || selectedList.length === 0) {
      return undefined
    }
    const firstId = selectedList[0]?.shipment_id || ""
    const key = `${selectedStatus}:${firstId}:${selectedList.length}`
    if (deliveryFocusKeyRef.current === key) return undefined
    deliveryFocusKeyRef.current = key
    const timer = window.setTimeout(() => {
      deliveryNameRef.current?.focus()
      deliveryNameRef.current?.select?.()
    }, 80)
    return () => window.clearTimeout(timer)
  }, [loading, selectedStatus, selectedList])

  useEffect(() => {
    fetchData()
  }, [mlSheetId])

  const fetchData = async () => {
    try {
      setLoading(true)

      // Fetch ML Sheet
      const mlResponse = await mlSheetService.getMLSheet(mlSheetId)
      const sheetData = mlResponse.data.ml_sheet || mlResponse.data.data
      const shipmentsData = mlResponse.data.shipments || []
      const canUpdate = mlResponse.data.can_update_status

      if (user?.role === 'Agent' || isContractPartyAdminRole(user)) {
        const allowed = canUpdate != null
          ? Boolean(canUpdate)
          : canUpdateMlSheetStatus(sheetData, user)
        if (!allowed) {
          Alert.error('You do not have permission to update status on this ML sheet')
          navigate(`/ml-sheets/view/${mlSheetId}`)
          return
        }
      }

      setMLSheet(sheetData)
      setShipments(Array.isArray(shipmentsData) ? shipmentsData : [])
    } catch (error) {
      console.error("Error fetching data:", error)
      Alert.error("Failed to load data")
      navigate("/ml-sheets")
    } finally {
      setLoading(false)
    }
  }

  // Check if user has permission to update status
  const canUpdateStatus = () => canUpdateMlSheetStatus(mlSheet, user)

  const filteredShipments = useMemo(
    () => shipments.filter((s) => shipmentMatchesSearch(s, searchQuery)),
    [shipments, searchQuery]
  )

  const selectableFilteredShipments = useMemo(
    () => filteredShipments.filter((s) => !isStatusUpdateLocked(s.status) && !s.is_deleted),
    [filteredShipments]
  )

  const updatableCount = useMemo(
    () => shipments.filter((s) => !isStatusUpdateLocked(s.status)).length,
    [shipments]
  )

  useEffect(() => {
    setSelectedShipments((prev) => {
      const lockedIds = new Set(
        shipments.filter((s) => isStatusUpdateLocked(s.status)).map((s) => s.shipment_id)
      )
      if (lockedIds.size === 0) return prev

      const next = new Set([...prev].filter((id) => !lockedIds.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [shipments])

  const allFilteredSelected =
    selectableFilteredShipments.length > 0 &&
    selectableFilteredShipments.every((s) => selectedShipments.has(s.shipment_id))

  const handleSelectAll = (e) => {
    const checked = e.target.checked
    const newSelected = new Set(selectedShipments)

    if (checked) {
      selectableFilteredShipments.forEach((s) => newSelected.add(s.shipment_id))
    } else {
      selectableFilteredShipments.forEach((s) => newSelected.delete(s.shipment_id))
    }
    setSelectedShipments(newSelected)
  }

  const handleSelectShipment = (shipmentId, status) => {
    if (isStatusUpdateLocked(status)) return

    const newSelected = new Set(selectedShipments)
    if (newSelected.has(shipmentId)) {
      newSelected.delete(shipmentId)
    } else {
      newSelected.add(shipmentId)
    }
    setSelectedShipments(newSelected)
  }

  const handleRevertDelivery = async (shipment) => {
    const blocked = revertDeliveryDisabledReason(shipment, user)
    if (blocked) {
      Alert.error(blocked)
      return
    }
    const confirmed = await Alert.confirm(
      `Revert delivery for ${shipment.shipment_id}? It will move back to Received, payment will become To-Pay, and collected amount will be reversed.`,
      "Revert Delivery",
    )
    if (!confirmed) return
    try {
      setRevertingId(shipment.shipment_id)
      await shipmentService.revertDelivery(shipment.shipment_id)
      Alert.success("Delivery reverted to Received")
      await fetchData()
    } catch (err) {
      Alert.error(err.response?.data?.error || "Failed to revert delivery")
    } finally {
      setRevertingId("")
    }
  }

  const handleBulkStatusUpdate = async () => {
    if (selectedShipments.size === 0) {
      Alert.error("Please select at least one shipment")
      return
    }

    if (!selectedStatus) {
      Alert.error("Please select a status")
      return
    }

    if (!bulkStatusConfig.options.includes(selectedStatus)) {
      Alert.error("Selected status is not allowed for the chosen shipments")
      return
    }

    if (selectedStatus === "Delivered" && user?.delivery_details_required !== false) {
      if (!String(deliveryReceiver || "").trim()) {
        Alert.error("Receiver name is required")
        return
      }
      if (!String(deliveryReceiverContact || "").trim()) {
        Alert.error("Receiver contact is required")
        return
      }
      if (!deliveryReceiverImage) {
        Alert.error("Delivery photo is required")
        return
      }
      if (!deliveryGovIdImage) {
        Alert.error("Government ID proof is required")
        return
      }
      const unpaid = Array.from(selectedShipments).filter((id) => {
        const shipment = shipments.find((s) => s.shipment_id === id)
        return shipment && requiresDeliveryCollection(shipment)
      })
      if (unpaid.length > 0) {
        Alert.error(
          `Cannot mark as Delivered until payment is complete. Unpaid: ${unpaid.join(", ")}`,
        )
        return
      }
    }

    try {
      setUpdating(true)

      const shipmentIds = Array.from(selectedShipments)

      const response = await shipmentService.bulkUpdateStatus(
        shipmentIds, 
        selectedStatus, 
        remarks,
        deliveryReceiver,
        deliveryReceiverImage,
        deliveryReceiverContact,
        showUnloadingInput ? unloadingCharges : null,
        showLoadingInput ? loadingCharges : null,
        deliveryGovIdImage,
        deliveryVehicleNo,
        lastMileHours,
      )

      if (isOtpRequiredResponse(response.data)) {
        setOtpPending(normalizeOtpPending(response.data))
        if (response.data.updated_count > 0) {
          Alert.success(`Updated ${response.data.updated_count} shipment(s). Verify OTP to deliver the rest.`)
        } else {
          Alert.success(response.data.message || "OTP sent on WhatsApp")
        }
        return
      }

      if (response.data?.success) {
        const ewayNote = (response.data.eway_bill_warnings || []).filter(Boolean).join(" ")
        Alert.success(`Successfully updated ${response.data.updated_count} shipment(s) to ${selectedStatus}${ewayNote ? `. ${ewayNote}` : ""}`)
        if (selectedStatus === "Delivered") {
          await printShipmentReceipts(shipmentIds, user)
        }

        // Reset selection and form
        setSelectedShipments(new Set())
        setSearchQuery("")
        setDeliveryReceiver("")
        setDeliveryReceiverContact("")
        setDeliveryReceiverImage(null)
        setImagePreview(null)
        setDeliveryGovIdImage(null)
        setGovIdPreview(null)

        // Refresh data
        await fetchData()
      } else {
        Alert.error(response.data?.error || "Failed to update shipments")
      }
    } catch (error) {
      console.error("Error updating shipments:", error)
      Alert.error(error.response?.data?.error || "Failed to update shipments")
    } finally {
      setUpdating(false)
    }
  }

  const getShipmentStatusColor = (status) => {
    const statusMap = {
      booked: "bg-blue-100 text-blue-800",
      dispatched: "bg-purple-100 text-purple-800",
      received: "bg-yellow-100 text-yellow-800",
      delivered: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      returned: "bg-orange-100 text-orange-800",
    }
    return statusMap[status?.toLowerCase()] || "bg-gray-100 text-gray-800"
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading Status Update..." />
        </div>
      </DashboardLayout>
    )
  }

  if (!canUpdateStatus()) {
    return (
      <DashboardLayout>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center bg-white rounded-xl p-6 shadow-lg max-w-md">
            <p className="text-red-600 text-lg font-semibold mb-4">🔒 Access Denied</p>
            <p className="text-gray-600 mb-6">You don't have permission to update shipment statuses.</p>
            <Button variant="secondary" onClick={() => navigate(`/ml-sheets/view/${mlSheetId}`)}>
              Back to ML Sheet
            </Button>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
                <div className={isMobileLayout ? "pb-24 min-h-0" : ""}>
                {/* Header */}
                {isMobileLayout ? (
                  <div className="sticky top-0 z-30 bg-white shrink-0 mb-2 pb-2 border-b border-gray-100">
                    <div className="flex items-center justify-between gap-2">
                      <h1 className="flex items-center gap-1.5 text-sm font-bold text-secondary truncate min-w-0">
                        <Package className="w-4 h-4 text-buttonhoverTxt shrink-0" />
                        <span className="truncate">{t('pages.mlSheet.statusUpdate')}</span>
                      </h1>
                      <Button
                        variant="secondary"
                        type="button"
                        size="sm"
                        icon={ArrowLeft}
                        mobileIconOnly
                        mobileLabel="Back to ML Sheet"
                        onClick={() => navigate(`/ml-sheets/view/${mlSheetId}`)}
                      />
                    </div>
                    <p className="text-[10px] text-gray-500 mt-1 truncate">#{mlSheetId}</p>
                  </div>
                ) : (
                <div className="p-4 sm:p-6 sm:px-0 mb-6 sm:py-2 sm:mb-2">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                      <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">{t('pages.mlSheet.statusUpdate')}</h1>
                    </div>
                    <Button variant="secondary" onClick={() => navigate(`/ml-sheets/view/${mlSheetId}`)} icon={ArrowLeft}>
                      Back
                    </Button>
                  </div>
                </div>
                )}

                {/* Status Update Form Card */}
                <div className="bg-white rounded-xl p-3 sm:p-4 mb-3 sm:mb-6 shadow-sm md:shadow-none">
                  <h3 className="hidden md:block text-base sm:text-lg font-semibold text-gray-700 mb-4 sm:mb-6">Update Status</h3>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-4">
                    {/* Status Dropdown */}
                    <div className="min-w-0">
                      <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                        Select Status <span className="text-red-500">*</span>
                      </label>
                      <SelectField
                        variant="form"
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        options={statusOptions}
                        className="min-w-0 w-full"
                      />
                      <p className="text-[9px] sm:text-xs text-gray-500 mt-1 leading-snug">
                        Dispatched → Received only. Received → Delivered when fully paid.
                      </p>
                      {bulkStatusConfig.paymentMessage && (
                        <p className="text-[9px] sm:text-xs text-amber-700 mt-1 leading-snug">{bulkStatusConfig.paymentMessage}</p>
                      )}
                    </div>

                    {/* Selection Count */}
                    <div className="min-w-0">
                      <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">Selected</label>
                      <div className="flex items-center justify-center px-2 sm:px-4 py-2 sm:py-3 border rounded-xl bg-blue-50/50 min-h-[38px] sm:min-h-[42px]">
                        <div className="text-xs sm:text-sm font-bold text-secondary">{selectedShipments.size}</div>
                        <div className="text-[10px] sm:text-sm text-secondary font-medium ml-1 truncate">
                          / {updatableCount} upd · {shipments.length} total
                        </div>
                      </div>
                    </div>
                    
                    {/* Remarks — full width on mobile */}
                    <div className="col-span-2 md:col-span-1 min-w-0">
                      <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">Remarks</label>
                      <textarea
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="Optional remarks..."
                        className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 resize-none md:border-2"
                        rows={isMobileLayout ? 2 : 1}
                      />
                    </div>
                  </div>

                  {(showUnloadingInput || showLoadingInput) && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-4 p-2 sm:p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <div className="sm:col-span-3">
                        <h4 className="text-[10px] sm:text-sm font-semibold text-blue-800">
                          Labour charges
                        </h4>
                      </div>
                      {showLoadingInput && (
                        <div className="min-w-0">
                          <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                            Loading charge
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={loadingCharges}
                            onChange={(e) => setLoadingCharges(e.target.value)}
                            placeholder="₹ loading"
                            className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                          />
                        </div>
                      )}
                      {showUnloadingInput && (
                      <div className="min-w-0">
                        <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                          Unloading charge
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={unloadingCharges}
                          onChange={(e) => setUnloadingCharges(e.target.value)}
                          placeholder="₹ unloading"
                          className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                        />
                      </div>
                      )}
                    </div>
                  )}

                  {/* Delivery Receiver Fields - Show only for Delivered status */}
                  {selectedStatus === "Delivered" && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-4 p-2 sm:p-4 bg-orange-50 rounded-xl border border-orange-200">
                      <div className="sm:col-span-3">
                <h4 className="text-[10px] sm:text-sm font-semibold text-orange-800">
                  Delivery proof {user?.delivery_details_required === false ? "(optional)" : "(required)"}
                </h4>
                      </div>
                      <div className="min-w-0">
                        <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                          Last-mile vehicle
                        </label>
                        <input
                          type="text"
                          value={deliveryVehicleNo}
                          onChange={(e) => setDeliveryVehicleNo(e.target.value.toUpperCase())}
                          placeholder="Needed when an e-Way Bill is on the GC"
                          className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                          maxLength={20}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                          E-Way close window
                        </label>
                        <select
                          value={lastMileHours}
                          onChange={(e) => setLastMileHours(e.target.value)}
                          className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                        >
                          <option value="1">1 hour</option>
                          <option value="2">2 hours</option>
                          <option value="3">3 hours</option>
                        </select>
                      </div>
                      <div className="min-w-0">
                        <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                          Receiver Name
                        </label>
                        <input
                          ref={deliveryNameRef}
                          type="text"
                          value={deliveryReceiver}
                          onChange={(e) => setDeliveryReceiver(e.target.value)}
                          onKeyDown={onEnter(() => deliveryContactRef.current?.focus())}
                          placeholder="Receiver name"
                          className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                          maxLength={250}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                          Receiver Contact
                        </label>
                        <input
                          ref={deliveryContactRef}
                          type="text"
                          value={deliveryReceiverContact}
                          onChange={(e) => setDeliveryReceiverContact(e.target.value.replace(/[^\d]/g, "").slice(0, 10))}
                          onKeyDown={onEnter(focusVisibleSubmit)}
                          placeholder="Contact number"
                          className="w-full px-2 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 md:border-2"
                          maxLength={10}
                        />
                      </div>
                      <div className="sm:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <CapturePhotoField
                        label="Delivery photo"
                        required={user?.delivery_details_required !== false}
                        file={deliveryReceiverImage}
                        preview={imagePreview}
                        disabled={updating}
                        onPick={(file) => {
                          setDeliveryReceiverImage(file)
                          const reader = new FileReader()
                          reader.onloadend = () => setImagePreview(reader.result)
                          reader.readAsDataURL(file)
                        }}
                        onClear={() => {
                          setDeliveryReceiverImage(null)
                          setImagePreview(null)
                        }}
                      />
                      <CapturePhotoField
                        label="Government ID proof"
                        required={user?.delivery_details_required !== false}
                        file={deliveryGovIdImage}
                        preview={govIdPreview}
                        disabled={updating}
                        onPick={(file) => {
                          setDeliveryGovIdImage(file)
                          const reader = new FileReader()
                          reader.onloadend = () => setGovIdPreview(reader.result)
                          reader.readAsDataURL(file)
                        }}
                        onClear={() => {
                          setDeliveryGovIdImage(null)
                          setGovIdPreview(null)
                        }}
                      />
                      </div>
                    </div>
                  )}

                  {/* Update Button — desktop only */}
                  <Button
                    variant="primary"
                    type="button"
                    data-ml-status-submit="true"
                    onClick={handleBulkStatusUpdate}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !updating) {
                        event.preventDefault()
                        handleBulkStatusUpdate()
                      }
                    }}
                    disabled={updating || selectedShipments.size === 0}
                    loading={updating}
                    className="hidden md:inline-flex"
                  >
                    {updating ? "Updating..." : `Update ${selectedShipments.size} Shipment(s)`}
                  </Button>
                </div>

                {/* Shipments List */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden max-w-full">
                  <div className="px-3 sm:px-6 py-2 sm:py-4 border-b border-gray-200 space-y-2 sm:space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={allFilteredSelected}
                          onChange={handleSelectAll}
                          disabled={selectableFilteredShipments.length === 0}
                          className="w-4 h-4 sm:w-5 sm:h-5 rounded border-2 border-gray-400 cursor-pointer accent-blue-500 disabled:opacity-40"
                        />
                        <h3 className="text-xs sm:text-lg font-semibold text-gray-700 mb-0">
                          Shipments ({filteredShipments.length}
                          {searchQuery.trim() ? `/${shipments.length}` : ""})
                        </h3>
                      </div>
                      <div className="flex-1 min-w-0">
                        <SearchSubmitInput
                          value={searchQuery}
                          onSearch={setSearchQuery}
                          placeholder="Exact match: ID, name, contact, ML sheet..."
                          ariaLabel="Search shipments"
                          inputClassName="w-full px-3 py-1.5 sm:py-2.5 border border-gray-300 rounded-xl text-[11px] sm:text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-orange-400"
                        />
                      </div>
                    </div>
                    {searchQuery.trim() && (
                      <p className="hidden sm:block text-xs text-gray-500">
                        Exact match on shipment ID, ML sheet, name, contact, or other shipment fields. Hyphens and spaces in IDs are treated the same.
                      </p>
                    )}
                  </div>

                  {shipments.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500 text-lg">No shipments in this ML Sheet</p>
                    </div>
                  ) : filteredShipments.length === 0 ? (
                    <div className="text-center py-12 px-4">
                      <p className="text-gray-500 text-lg">No shipments match your search</p>
                      <button
                        type="button"
                        onClick={() => setSearchQuery("")}
                        className="mt-3 text-sm text-orange-600 font-semibold hover:underline"
                      >
                        Clear search
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Desktop table */}
                      <div className="hidden xl:block w-full overflow-x-auto">
                        <table className="w-full min-w-[900px] border-collapse">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                              <th className="px-4 py-3 text-left w-12">
                                <input
                                  type="checkbox"
                                  checked={allFilteredSelected}
                                  onChange={handleSelectAll}
                                  disabled={selectableFilteredShipments.length === 0}
                                  className="w-4 h-4 rounded border border-gray-400 cursor-pointer accent-blue-500"
                                />
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                                Shipment ID
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap min-w-[140px]">
                                Sender
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap min-w-[140px]">
                                Receiver
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                                Amount
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                                Current Status
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {filteredShipments.map((shipment, index) => {
                              const locked = isStatusUpdateLocked(shipment.status)
                              const canRevert = canRevertDelivery(shipment, user)
                              const revertBlockedReason = revertDeliveryDisabledReason(shipment, user)
                              return (
                              <tr
                                key={shipment.id || shipment.shipment_id}
                                className={`hover:bg-gray-50 transition-colors ${
                                  index % 2 === 0 ? "bg-white" : "bg-gray-50"
                                } ${locked ? "bg-green-50/40" : ""} ${
                                  selectedShipments.has(shipment.shipment_id) ? "ring-1 ring-inset ring-orange-300" : ""
                                }`}
                              >
                                <td className="px-4 py-3">
                                  <input
                                    type="checkbox"
                                    checked={selectedShipments.has(shipment.shipment_id)}
                                    onChange={() => handleSelectShipment(shipment.shipment_id, shipment.status)}
                                    disabled={locked}
                                    title={locked ? "Delivered shipments cannot be updated" : undefined}
                                    className="w-4 h-4 rounded border border-gray-400 cursor-pointer accent-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                                  />
                                </td>
                                <td className="px-4 py-3 text-sm whitespace-nowrap">
                                  <span
                                    onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                                    className="font-semibold text-secondary hover:text-orange-600 hover:underline cursor-pointer"
                                  >
                                    {shipment.shipment_id}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm min-w-[140px]">
                                  <div className="font-medium text-gray-900 break-words">{shipment.sender_name}</div>
                                  <div className="text-xs text-gray-500">{shipment.sender_contact}</div>
                                  {shipment.sender_location && (
                                    <div className="text-xs text-gray-400 mt-0.5 break-words">{shipment.sender_location}</div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm min-w-[140px]">
                                  <div className="font-medium text-gray-900 break-words">{shipment.receiver_name}</div>
                                  <div className="text-xs text-gray-500">{shipment.receiver_contact}</div>
                                  {shipment.receiver_location && (
                                    <div className="text-xs text-gray-400 mt-0.5 break-words">{shipment.receiver_location}</div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm whitespace-nowrap">
                                  <span className="font-semibold text-gray-900">
                                    ₹{shipment.total_amount?.toFixed(2) || "0.00"}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm whitespace-nowrap">
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase ${getShipmentStatusColor(
                                      shipment.status,
                                    )}`}
                                  >
                                    {shipment.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
                                  <div className="flex items-center justify-center gap-2">
                                    {locked && (
                                      canRevert ? (
                                        <button
                                          type="button"
                                          onClick={() => handleRevertDelivery(shipment)}
                                          disabled={revertingId === shipment.shipment_id}
                                          title="Revert delivery to Received"
                                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-amber-700 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                          <Undo2 className={`w-4 h-4 ${revertingId === shipment.shipment_id ? "animate-spin" : ""}`} />
                                        </button>
                                      ) : (
                                        <span
                                          title={revertBlockedReason || "Revert is not available"}
                                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 bg-gray-100 cursor-not-allowed"
                                          aria-disabled="true"
                                        >
                                          <Ban className="w-4 h-4" />
                                        </span>
                                      )
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                                      className="text-orange-600 hover:text-orange-700 font-semibold text-sm hover:underline"
                                    >
                                      View →
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Tablet & mobile cards */}
                      <div className="xl:hidden space-y-2 p-2 sm:p-4">
                        {filteredShipments.map((shipment) => {
                          const isSelected = selectedShipments.has(shipment.shipment_id)
                          const locked = isStatusUpdateLocked(shipment.status)
                          const canRevert = canRevertDelivery(shipment, user)
                          const revertBlockedReason = revertDeliveryDisabledReason(shipment, user)
                          return (
                            <div
                              key={shipment.id || shipment.shipment_id}
                              className={`bg-white rounded-xl shadow-sm border p-2.5 sm:p-4 border-l-4 transition-colors ${
                                locked
                                  ? "border-gray-200 border-l-green-500 bg-green-50/30"
                                  : isSelected
                                  ? "border-orange-400 border-l-orange-500 bg-orange-50/40"
                                  : "border-gray-100 border-l-orange-500"
                              }`}
                            >
                              <div className="flex items-start gap-2 mb-2">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleSelectShipment(shipment.shipment_id, shipment.status)}
                                  disabled={locked}
                                  title={locked ? "Delivered shipments cannot be updated" : undefined}
                                  className="w-4 h-4 rounded border-2 border-gray-400 cursor-pointer accent-blue-500 mt-0.5 shrink-0 disabled:cursor-not-allowed disabled:opacity-50"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex flex-wrap items-start justify-between gap-1">
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                                      className="text-orange-600 font-bold text-xs sm:text-sm break-all hover:underline text-left"
                                    >
                                      {shipment.shipment_id}
                                    </button>
                                    <span
                                      className={`px-1.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold uppercase shrink-0 ${getShipmentStatusColor(
                                        shipment.status,
                                      )}`}
                                    >
                                      {shipment.status}
                                    </span>
                                  </div>
                                  <p className="text-xs sm:text-sm font-bold text-gray-900 mt-1">
                                    ₹{shipment.total_amount?.toFixed(2) || "0.00"}
                                  </p>
                                  {locked && (
                                    canRevert ? (
                                      <button
                                        type="button"
                                        onClick={() => handleRevertDelivery(shipment)}
                                        disabled={revertingId === shipment.shipment_id}
                                        title="Revert delivery to Received"
                                        className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 disabled:opacity-40"
                                      >
                                        <Undo2 className={`w-3.5 h-3.5 ${revertingId === shipment.shipment_id ? "animate-spin" : ""}`} />
                                        Revert delivery
                                      </button>
                                    ) : (
                                      <span
                                        title={revertBlockedReason || "Revert is not available"}
                                        className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-gray-400 cursor-not-allowed"
                                        aria-disabled="true"
                                      >
                                        <Ban className="w-3.5 h-3.5" />
                                        Revert disabled
                                      </span>
                                    )
                                  )}
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-2 text-[10px] sm:text-sm">
                                <div className="min-w-0">
                                  <p className="text-gray-600 font-medium">Sender</p>
                                  <p className="font-semibold text-gray-900 break-words leading-tight">{shipment.sender_name}</p>
                                  <p className="text-gray-600">{shipment.sender_contact}</p>
                                </div>
                                <div className="min-w-0">
                                  <p className="text-gray-600 font-medium">Receiver</p>
                                  <p className="font-semibold text-gray-900 break-words leading-tight">{shipment.receiver_name}</p>
                                  <p className="text-gray-600">{shipment.receiver_contact}</p>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </>
                  )}
                </div>
                </div>

                {isMobileLayout && (
                  <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] md:hidden">
                    <Button
                      variant="primary"
                      type="button"
                      icon={RefreshCw}
                      data-ml-status-submit="true"
                      onClick={handleBulkStatusUpdate}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && !updating) {
                          event.preventDefault()
                          handleBulkStatusUpdate()
                        }
                      }}
                      disabled={updating || selectedShipments.size === 0}
                      loading={updating}
                      fullWidth
                      className="w-full justify-center"
                    >
                      {updating ? "Updating..." : `Update ${selectedShipments.size} Shipment(s)`}
                    </Button>
                  </div>
                )}
      <DeliveryOtpModal
        open={otpPending.length > 0}
        pending={otpPending}
        onClose={() => setOtpPending([])}
        onVerified={async () => {
          const deliveredIds = otpPending.map((row) => row.shipment_id).filter(Boolean)
          setOtpPending([])
          setSelectedShipments(new Set())
          setSearchQuery("")
          setDeliveryReceiver("")
          setDeliveryReceiverContact("")
          setDeliveryReceiverImage(null)
          setImagePreview(null)
          setDeliveryGovIdImage(null)
          setGovIdPreview(null)
          Alert.success("Delivery verified and completed")
          await printShipmentReceipts(deliveredIds, user)
          await fetchData()
        }}
      />
    </DashboardLayout>
  )
}

export default StatusUpdate